import React, { useEffect, useRef, useState } from "react";
import { AiOutlineSearch } from "react-icons/ai";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";
import { MdDeleteForever, MdEdit, MdImage } from "react-icons/md";
import { Link } from "react-router-dom";
import { useSelector } from 'react-redux';

import { Alert, IconButton } from "@material-ui/core";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableFooter from '@mui/material/TableFooter';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import swal from "@sweetalert/with-react";
import { Form } from "@unform/web";
import { toast } from "react-toastify";
import * as Yup from "yup";
import DivDiagonal from "../../components/DivDiagonal";
import CustomButton from "../../components/Form/Button";
import Input from "../../components/Form/Input";
import DeleteStockMovementModal from "./DeleteStockMovementModal";
import messageTypes from "../../constants/MessagesTypes";
import { getStockMovements, deleteStockMovement } from "../../services/StockMovement";
import {
  StockMovementWrapper,
  PageHeader,
  NewMovementButton,
  StockMovementCard,
  FilterSection,
  FilterTitle,
  FormWrapper,
  StyledFilterFormWrapper,
  ContentArea,
  StyledTableContainer,
  ProductImage,
  ProductInfo,
  MovementBadge,
  ActionButtons,
  PaginationInfo,
  PaginationButton,
  LoadingButton,
  EmptyState,
  TableWrapper,
  FilterRow,
  FilterInputWrapper,
  FilterButtonWrapper
} from "./styles";

export default function StockMovement() {
  const { currentCompany } = useSelector((state) => state.CompanyReducer);
  const [loading, setLoading] = useState(false);
  const [deleteStockMovementId, setDeleteStockMovementId] = useState(null);
  const [total, setTotal] = useState(0);
  const [stockMovements, setStockMovements] = useState([]);
  const [page, setPage] = useState(1);

  const formRef = useRef(null);

  const [filter, setFilter] = useState({
    stringSearch: '',
  });

  useEffect(() => {
    setLoading(true);
    
    // Resetar página quando a empresa mudar
    if (currentCompany?.id) {
      setPage(1);
    }

    getStockMovements({
      page: page,
      per_page: 25,
      stringSearch: filter.stringSearch,
    }).then((data) => {
      if (!data || data === '') {
        return;
      }

      setTotal(data.data.total);
      setStockMovements(data.data);
      setLoading(false);
    });

    return function cleanup() {
      setStockMovements([]);
    };
  }, [page, deleteStockMovementId, filter.stringSearch, currentCompany?.id]);

  async function handleSubmit(data) {
    try {

      formRef.current.setErrors({});
      const schema = Yup.object().shape({});

      await schema.validate(data, {
        abortEarly: false,
      });

      setFilter({
        stringSearch: data.stringSearch,
      });

    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const validationErrors = {};
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message;
        });
        formRef.current.setErrors(validationErrors);
        toast.warning(messageTypes.REQUIRED_FIELDS);
        return;
      }

      toast.error(messageTypes.ERROR);
    }
  }

  function getMovementTypeLabel(movementType) {
    return movementType === 'ENTRADA' ? 'Entrada' : 'Saída';
  }

  function getMovementTypeBadgeClass(movementType) {
    return movementType === 'ENTRADA' ? 'badge-success' : 'badge-danger';
  }

  function formatDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  }

  function handleDelete(id) {
    setDeleteStockMovementId(id);
  }

  async function handleConfirmDelete() {
    try {
      await deleteStockMovement(deleteStockMovementId);
      toast.success(messageTypes.SUCCESS_DELETE || 'Movimentação excluída com sucesso!');
      setDeleteStockMovementId(null);
      // Recarregar a lista
      setLoading(true);
      getStockMovements({
        page: page,
        per_page: 25,
        stringSearch: filter.stringSearch,
      }).then((data) => {
        if (!data || data === '') {
          setStockMovements([]);
          setTotal(0);
          setLoading(false);
          return;
        }

        setTotal(data.data.total || 0);
        setStockMovements(data.data || []);
        setLoading(false);
        // Ajustar página se necessário
        if (stockMovements.data && stockMovements.data.length === 1 && page > 1) {
          setPage(page - 1);
        }
      }).catch(() => {
        setStockMovements([]);
        setTotal(0);
        setLoading(false);
      });
    } catch (error) {
      toast.error(error.response?.data?.message || messageTypes.ERROR_DELETE || 'Erro ao excluir movimentação');
      setDeleteStockMovementId(null);
    }
  }

  function showImage(urlImage) {
    swal(
      {
        showCloseButton: true,
        className: 'img-perfil-modal',
        confirmButtonText: 'Fechar',
        content: <div>
          <div className={"img-perfil"}>
            <img src={urlImage} width={'80%'} height={"80%"} alt={'Imagem do produto'}/>
          </div>
        </div>,
      }
    );
  }

  return (
    <StockMovementWrapper>
      <DivDiagonal/>
      <ContentArea>
        <PageHeader>
          <h1>
            <i className="fa fa-exchange-alt"/> Estoque <span className="badge">{total}</span>
          </h1>
          <NewMovementButton as={Link} to="/stock-movement/new">
            <i className="fas fa-plus"/> Nova movimentação
          </NewMovementButton>
        </PageHeader>
        
        <StockMovementCard>
          <FilterSection>
            <StyledFilterFormWrapper>
              <Form ref={formRef} onSubmit={handleSubmit}>
                <FilterTitle>
                  <i className="fas fa-funnel-dollar"/> Filtro
                </FilterTitle>
                <FilterRow>
                  <FilterInputWrapper>
                    <Input
                      type="text"
                      name="stringSearch"
                      placeholder={"Buscar por ID, nome do produto ou código de barras"}
                      label={'Pesquisar movimentação'}
                    />
                  </FilterInputWrapper>
                  <FilterButtonWrapper>
                    <CustomButton
                      className="btn btn-primary font-weight-bold text-capitalize mobile-button"
                      type="submit"
                      startIcon={<AiOutlineSearch/>}
                      style={{
                        background: 'linear-gradient(135deg, #f29205 0%, #ff6b35 100%)',
                        border: 'none',
                        color: '#ffffff',
                        padding: '0 32px',
                        borderRadius: '10px',
                        fontWeight: 600,
                        boxShadow: '0 4px 15px rgba(242, 146, 5, 0.3)',
                        transition: 'all 0.3s ease',
                        width: 'auto',
                        minWidth: 'auto',
                        height: '56px',
                        display: 'flex',
                        alignItems: 'center'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(242, 146, 5, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(242, 146, 5, 0.3)';
                      }}
                    >
                      Buscar
                    </CustomButton>
                  </FilterButtonWrapper>
                </FilterRow>
              </Form>
            </StyledFilterFormWrapper>
          </FilterSection>
          
          <TableWrapper>
            {loading ? (
              <LoadingButton
                className="btn btn-primary text-capitalize font-weight-bold"
                loading={loading}
                type="button"
              >
                Carregando...
              </LoadingButton>
            ) : (
              <>
                {stockMovements && stockMovements.data && stockMovements.data.length ? (
                  <StyledTableContainer>
                    <Table sx={{minWidth: 650}} aria-label="caption table">
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">Tipo</TableCell>
                          <TableCell>Produto</TableCell>
                          <TableCell align="center">Quantidade</TableCell>
                          <TableCell align="center">Opções</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {stockMovements.data.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell align="center">
                              <MovementBadge className={getMovementTypeBadgeClass(item.movementType)}>
                                {getMovementTypeLabel(item.movementType)}
                              </MovementBadge>
                            </TableCell>
                            <TableCell>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '200px' }}>
                                <ProductImage
                                  onClick={() => {
                                    if (item.product && item.product.urlPhoto) {
                                      showImage(`${process.env.REACT_APP_API_ENDPOINT}${item.product.urlPhoto}`);
                                    }
                                  }}
                                >
                                  {item.product && item.product.urlPhoto ? (
                                    <img
                                      src={`${process.env.REACT_APP_API_ENDPOINT}${item.product.urlPhoto}`}
                                      alt={"Imagem do produto"}
                                    />
                                  ) : (
                                    <MdImage />
                                  )}
                                </ProductImage>
                                <ProductInfo>
                                  <div className="product-name">
                                    {item.product ? (
                                      item.product.brand 
                                        ? `${item.product.name} - ${item.product.brand}`
                                        : item.product.name
                                    ) : (
                                      'Produto não encontrado'
                                    )}
                                  </div>
                                  <div className="product-details">
                                    {item.product && item.product.barcode && `Código de barras: ${item.product.barcode}`}
                                    {item.product && item.product.barcode && <br />}
                                    Data: {formatDate(item.movementDate)}
                                    {item.description && (
                                      <>
                                        <br />
                                        <span style={{ fontStyle: 'italic', color: 'rgba(255, 255, 255, 0.7)' }}>
                                          {item.description}
                                        </span>
                                      </>
                                    )}
                                  </div>
                                </ProductInfo>
                              </div>
                            </TableCell>
                            <TableCell align="center">
                              {item.quantity}
                            </TableCell>
                            <TableCell align="center">
                              <ActionButtons>
                                <Link to={`/stock-movement/${item.id}/edit`}>
                                  <IconButton aria-label="edit">
                                    <MdEdit/>
                                  </IconButton>
                                </Link>
                                <IconButton
                                  aria-label="delete"
                                  onClick={() => handleDelete(item.id)}
                                >
                                  <MdDeleteForever/>
                                </IconButton>
                              </ActionButtons>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      <TableFooter>
                        <TableRow>
                          <TableCell colSpan="4" style={{ border: 'none' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                              <PaginationInfo>
                                Página {page} de {stockMovements.total_page + 1}
                              </PaginationInfo>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <PaginationButton
                                  type="button"
                                  onClick={() => setPage(page - 1)}
                                  disabled={page <= 1}
                                >
                                  <IoIosArrowBack/> Anterior
                                </PaginationButton>
                                <PaginationButton
                                  type="button"
                                  onClick={() => setPage(page + 1)}
                                  disabled={!stockMovements.next_page}
                                >
                                  Próxima <IoIosArrowForward/>
                                </PaginationButton>
                              </div>
                            </div>
                          </TableCell>
                        </TableRow>
                      </TableFooter>
                    </Table>
                  </StyledTableContainer>
                ) : (
                  <EmptyState>
                    <Alert severity="warning">{messageTypes.REGISTER_NOT_FOUNT}</Alert>
                  </EmptyState>
                )}
              </>
            )}
          </TableWrapper>
        </StockMovementCard>
      </ContentArea>

      <DeleteStockMovementModal
        isOpen={!!deleteStockMovementId}
        onClose={() => setDeleteStockMovementId(null)}
        onConfirm={handleConfirmDelete}
        movementId={deleteStockMovementId}
      />
    </StockMovementWrapper>
  );
}
